import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, startWith, map } from 'rxjs';
import { DisbursmentService } from 'src/app/shared/service/disbursment/disbursment.service';
import { allowOnlyLetters, allowLimitedInput } from '../../master.utils';
import { ToastrService } from 'ngx-toastr';
import { PortService } from 'src/app/shared/service/disbursment/port.service';
import { MaCountry } from './country.model';
import { TariffService, TariffSubService, TariffRequest } from './create-tariff.model';
import { MaPort } from './port.model';
import { MultipleVesselCreationComponent } from './multiple-vessel-creation/multiple-vessel-creation.component';
import { CreateTariffComponent } from '../create-tariff/create-tariff.component';
import { ConfirmationDialogComponent } from './confirmation-dialog.component';

@Component({
  selector: 'app-create-port-tariff',
  templateUrl: './create-port-tariff.component.html',
  styleUrls: ['./create-port-tariff.component.scss']
})
export class CreatePortTariffComponent {
 private loader = inject(DisbursmentService);
 isLoading$ = this.loader.loading$;
  disallowNumericData = allowOnlyLetters;
  allowLimitedInput = allowLimitedInput;
  portForm!: FormGroup;
  isEditMode = false;
  isDropdownOpen = false;
  portChecked: boolean = false;
  countries: MaCountry[] = [];
  filteredCountries$!: Observable<MaCountry[]>;

  ports: MaPort[] = [];
  filteredPorts$!: Observable<MaPort[]>;
  get countryControl() {
    return this.portForm.get('country_id');
  }
  displayedColumns: string[] = [
    // 'dragHandle',
    'sno',
    'service',
    'info_msg',
    'subservice',
    // 'purpose',
    'basic_value',
    'movement',
    'tariff_percent',
    'formula_result',
    'optional',
    'action'
  ];

  infoRowColumns: string[] = ['info_msg'];
  dataSource = new MatTableDataSource<any>();
  tableDataSource = new MatTableDataSource<any>();
  searchTerm: string = '';
  selectedCountryId: number | null = null;

  // Master data for create-tariff component
  masterData: any = {
    vslProperties: [],
    portServices: [],
    purposes: []
  };
  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public existingdata: any,
    private portService: PortService,
    private snackBar: ToastrService,
    private dialogRef: MatDialogRef<CreatePortTariffComponent>
  ) {
    const rowData = existingdata?.rowData;


    // Initialize table data if we have existing data
    if (rowData && rowData.rules) {
      // Initialize dataSource with consistent structure - only serviceObj
      this.dataSource.data = rowData.rules.items.map((rule: any, index: number) => {
        const serviceObj = {
          SNO: index + 1, // Generate SNO dynamically based on position
          code: rule.code,
          service: rule.service,
          purpose: rule.purpose,
          info_msg: rule.info_msg,
          sub_services: rule.subService.map((sub: any) => {

            return {
              ...sub,
              optional: sub.optional === 'Y' || sub.optional === true,
              hide: sub.hide === 'Y' || sub.hide === true,
            };
          }),
          total: rule.total || 0
        };
        return { serviceObj };
      });
      // Update table with dynamic SNO
      this.updateTable();
    }
    this.isEditMode = !!rowData;
    // Populate vessel fields from rowData
    const vesselFieldsArray = (rowData?.vessel_fields || []).map((vf: any) =>
      this.fb.group({
        field_name: [vf?.field_name || '', Validators.required],
        data_type: [vf?.data_type || '', Validators.required],
        is_mandatory: [vf?.is_mandatory || false, Validators.required]
      })
    );

    this.portForm = this.fb.group({
      country_id: [rowData?.country_id || 0, Validators.required],
      port_id: [rowData?.port_id || '', Validators.required],
      vessel_fields: this.fb.array(vesselFieldsArray)
    });
  }
  ngOnInit() {
    this.loadMasterData();
    this.filteredCountries$ = this.portForm.get('country_id')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCountries(value || ''))
    );

    this.filteredPorts$ = this.portForm.get('port_id')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterPorts(value || ''))
    );
  }

  toCopyRule(event: any) {
    this.portChecked = event.checked;
  }

  // Helper method to regenerate SNO dynamically based on array position
  private regenerateSNOs(): void {
    this.dataSource.data.forEach((item, index) => {
      if (item.serviceObj) {
        item.serviceObj.SNO = index + 1;
      }
    });
  }

  isFirstSubserviceRow = (_: number, row: any): boolean => {
    const data = this.tableDataSource.data;
    const index = data.indexOf(row);
    if (index === -1) {
      return false;
    }
    const currentKey = `${row.service}|${row.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;
    return index === 0 || prevKey !== currentKey;
  };

  tarifdataManupulation(rowData: any): any[] {
    if (!rowData || !rowData.rules || !rowData.rules.items) {
      return [];
    }

    return rowData.rules.items.reduce((acc: any[], item: any) => {
      const enriched = item.subService.map((sub: any) => {
        return {
          ...sub,
          code: item.code,
          service: item.service,
          sno: item.SNO,
          formula_result: sub.resultformula || sub.formula_result,
          optional: sub.optional === 'Y' || sub.optional === true,
          hide: sub.hide === 'Y' || sub.hide === true,
          purpose: item?.purpose || null,
          basic_value: sub.basic_value || '',
          tariff_percent: sub.tariff_percent || '',
        };
      });
      return acc.concat(enriched);
    }, []);
  }


  private _filterCountries(value: string | number): MaCountry[] {
    if (typeof value === 'number') {
      return this.countries.filter(country => country.country_id === value);
    }
    const filterValue = value.toString().toLowerCase();
    return this.countries.filter(country =>
      (country.name ?? '').toLowerCase().includes(filterValue)
    );
  }

  private _filterPorts(value: string | number): MaPort[] {
    if (typeof value === 'number') {
      return this.ports.filter(port => port.port_id === value);
    }
    const filterValue = value.toString().toLowerCase();
    const result = this.ports.filter(port =>
      (port.name ?? '').toLowerCase().includes(filterValue)
    );

    return result;
  }

  onPortSelected(name: string): void {
    let selectedPortId: number = 0;
    const selected = this.ports.find(p => p.name === name);
    selectedPortId = selected?.port_id || 0;
    this.portForm.get('port_id')?.setValue(selectedPortId);

  }

  onCountrySelected(name: string): void {
    let selectedCountryId: number = 0;
    const selected = this.countries.find(c => c.name === name);
    selectedCountryId = selected?.country_id || 0;
    this.portForm.get('country_id')?.setValue(selectedCountryId);
    if (selectedCountryId != 0) {
      this.loadPortsByCountryId(selectedCountryId);
    }
  }

  onCountryInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (!value) {
      this.portForm.get('country_id')?.setValue('');
    }
  }

  onPortInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (!value) {
      this.portForm.get('port_id')?.setValue('');
    }
  }

  loadPortsByCountryId(id: number): void {
    this.portService.portsByCountry(id).subscribe({
      next: (data) => {
        this.ports = data;
        // Reset port control and update filtered ports
        this.portForm.get('port_id')?.setValue('');
        this.filteredPorts$ = this.portForm.get('port_id')!.valueChanges.pipe(
          startWith(''),
          map(value => this._filterPorts(value || ''))
        );
        const existingId = this.existingdata?.rowData.port_id;
        if (existingId) {
          this.portForm.patchValue({ port_id: existingId });
        }

      },
      error: (err) => {
        console.error('Error fetching ports:', err);
      }
    });
  }


  displayCountryFn = (countryId: number | null): string => {
    if (!countryId || this.countries.length === 0) return '';
    const country = this.countries.find(c => c.country_id === countryId);
    return country && country.name ? country.name : '';
  };

  displayPortFn = (id: number): string => {
    if (!id || this.ports.length === 0) return '';
    const port = this.ports.find(c => c.port_id === id);
    return port && port.name ? port.name : '';
  };

  // onPaste method removed as name field is no longer needed

  get vesselFields() {
    return this.portForm.get('vessel_fields') as any;
  }

  // method to add the additional vessel fields
  addVesselField() {
    this.vesselFields.push(
      this.fb.group({
        field_name: ['', Validators.required],
        data_type: ['', Validators.required],
        is_mandatory: [false, Validators.required]
      })
    );
  }

  // method to remove the additional vessel fields
  removeVesselField(index: number) {
    this.vesselFields.removeAt(index);
  }

  // Load master data for create-tariff component
  loadMasterData() {
    this.loader.showDialogLoader();
    const body = { fields: ["country", "vessel_properties", "port_services", "purpose", "tariff_services"] };
    this.portService.getMasterData(body).subscribe({
      next: (response) => {
        this.countries = response.country || [];
        this.filteredCountries$ = this.portForm.get('country_id')!.valueChanges.pipe(
          startWith(''),
          map(value => this._filterCountries(value || ''))
        );

        const existingId = this.existingdata?.rowData.country_id;
        if (existingId) {
          this.portForm.patchValue({ country_id: existingId });
          this.loadPortsByCountryId(existingId);
        }

        this.masterData.vslProperties = response.vessel_properties || [];
        this.masterData.portServices = response.port_services || [];
        this.masterData.purposes = response.purpose || [];
        this.masterData.tariffServices = response.tariff_services || [];
        this.loader.hideDialogLoader();
      },
      error: (error) => {
        this.loader.hideDialogLoader();
        const errorMsg = error.error?.error || 'Something went wrong, Unable to load Suggestions. Please try again.';
        this.snackBar.error(errorMsg);
      }
    });
  }
  // Port Tariff Submit
  onSubmitPortTariff() {
    // Check which controls are invalid
    Object.keys(this.portForm.controls).forEach(key => {
      const control = this.portForm.get(key);
    });

    // Check if country_id and port_id are valid
    const countryIdValid = this.portForm.get('country_id')?.valid;
    const portIdValid = this.portForm.get('port_id')?.valid;

    if (!countryIdValid || !portIdValid) {
      this.portForm.markAllAsTouched();
      this.snackBar.error('Please select country and port');
      return;
    }

    const formData = this.portForm.value;

    // Group tariffs by service
    const serviceMap = new Map();

    // Process each tariff item in the dataSource
    this.dataSource.data.forEach((item: any) => {
      if (item.serviceObj) {
        // Use service name as key to avoid overwriting services with same code
        const key = `${item.serviceObj.service || item.serviceObj.code}|${item.serviceObj.purpose || ''}`;
        serviceMap.set(key, item.serviceObj);
      }
    });

    // Convert the map to an array
    const rules = Array.from(serviceMap.values());

    // Validate that we have at least one rule
    if (rules.length === 0) {
      this.snackBar.error('Please add at least one tariff');
      return;
    }

    // Get the port name from the selected port
    const selectedPort = this.ports.find(p => p.port_id === formData.port_id);
    let portName = selectedPort?.name || '';

    if (selectedPort == undefined) {
      portName = formData.port_id;
    }

    // Create TariffRequest structure - include all services in items array
    const allTariffServices = rules.map((rule, index) => new TariffService({
      SNO: index + 1, // Generate SNO dynamically based on position
      code: rule.code || null,
      total: rule.total || '',
      service: rule.service || null,
      purpose: rule.purpose || null,
      pa_remark: rule.pa_remark || null,
      meraki_feedback: rule.meraki_feedback || null,
      meraki_remark_client: rule.meraki_remark_client || null,
      client_comment: rule.client_comment || null,
      info_msg: rule.info_msg || null,
      negotiate: rule.negotiate || 'N',
      agreed: rule.agreed || 'N',
      client_option: rule.client_option || 'N',
      display_to_client: rule.display_to_client || 'N',
      subService: (rule.sub_services || rule.subService || []).map((sub: any) => new TariffSubService({
        name: sub.name || null,
        unique_key: rule.purpose != null
          ? `${rule.service || ''}${rule.purpose ? `|${rule.purpose}` : ''}${sub.name ? `|${sub.name}` : ''}`
          : `${rule.service || ''}${sub.name ? `|${sub.name}` : ''}`,
        basic_value: sub.basic_value || null,
        movement: sub.movement || null,
        tariff_percent: sub.tariff_percent || null,
        calculated_basic_value: null,
        formula_result: sub.formula_result || null,
        formula_inputs: null,
        optional: sub.optional ? 'Y' : 'N',
        hide: sub.hide ? 'Y' : 'N',
        operational_flag: '+',
        sub_total: 0,
      }))
    }));

    const tariffRequest = new TariffRequest({
      items: allTariffServices
    });

    const portData: any = {
      port_tariff_rule_id: formData.port_tariff_rule_id || null,
      port_id: formData.port_id,
      name: portName,
      country_id: formData.country_id,
      rules: tariffRequest,
      status: "Y",
      vessel_fields: formData.vessel_fields || []
    };
    if (this.portChecked) {
      portData.port_tariff_rule_id = 0;
    }
    if (selectedPort == undefined) {
      portData.port_id = 0;
    }
    // Submit the data to the API
    this.loader.show();
    this.portService.saveTariffRulesDataToDB(portData).subscribe({
      next: (res) => {
        this.loader.hide();
        this.snackBar.success(res.message || 'Port tariff saved successfully');
        this.dialogRef.close("refresh");
      },
      error: (error) => {
        this.loader.hide();
        const errorMsg = error.error?.error || 'Something went wrong. Please try again.';
        this.snackBar.error(errorMsg);
      }
    });
  }
  // method to close the dialog
  closeDialog() {
    this.dialogRef.close();
  }

  onSearch(): void {
  }

  clearSearch(): void {
    this.searchTerm = '';
  }


  // Method to open Multiple Vessel Creation Dialog
  openMultipleVesselCreationDialog() {

    const vesselData = this.vesselFields.value?.map((vessel: any) => {
      const dataType = this.splitDataType(vessel.data_type)
      return {
        field_name: vessel.field_name,
        data_type: dataType?.type,
        is_mandatory: vessel.is_mandatory,
        dropdown_input: dataType?.data
      }
    })
    const dialogConfig: MatDialogConfig = {
      width: '1200px',
      minWidth: '1200px',
      height: '450px',
      disableClose: true,
      data: {
        vessels: vesselData,
        isEditMode: false
      }
    };
    const dialogRef = this.dialog.open(MultipleVesselCreationComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && Array.isArray(result)) {
        // Clear existing fields
        while (this.vesselFields.length !== 0) {
          this.vesselFields.removeAt(0);
        }
        // Add all new vessels
        result.forEach((vessel: any) => {
          const vesselGroup = this.fb.group({
            field_name: [vessel.field_name?.trim(), Validators.required],
            data_type: `${vessel.data_type}:${vessel.dropdown_input}`,
            is_mandatory: [vessel.is_mandatory || 'N', Validators.required]
          });
          this.vesselFields.push(vesselGroup);
        });
      }
    });
  }

  // Method to edit single vessel field using multiple vessel creation component
  openEditSingleVesselField(fieldData: any, index: number) {

    const dataType = this.splitDataType(fieldData.data_type)
    const formattedData = {
      field_name: fieldData.field_name,
      data_type: dataType?.type,
      is_mandatory: fieldData.is_mandatory,
      dropdown_input: dataType?.data

    }
    const dialogConfig: MatDialogConfig = {
      width: '1200px',
      minWidth: '1200px',
      height: '450px',
      disableClose: true,
      data: {
        vessels: [formattedData],
        isEditMode: true
      }
    };

    const dialogRef = this.dialog.open(MultipleVesselCreationComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && Array.isArray(result) && result.length > 0) {
        // Update the specific field at the given index
        const updatedField = result[0];
        this.vesselFields.at(index).patchValue({
          field_name: updatedField.field_name?.trim(),
          data_type: `${updatedField.data_type}:${updatedField.dropdown_input}`,
          is_mandatory: updatedField.is_mandatory || 'N',
        });
      }
    });
  }



  // Method to open Add or Edit Tariff Dialog
  openAddOrEditTariffDialog(rowData?: any, index?: number) {
    const screenWidth = window.innerWidth;
    const minWidthPx = screenWidth * 0.73;
    const maxWidthPx = screenWidth * 0.92;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.position = { right: '0' };
    dialogConfig.height = '100%';
    dialogConfig.minWidth = `${minWidthPx}px`;
    dialogConfig.maxWidth = `${maxWidthPx}px`;
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = ['animate__animated', 'animate__slideInRight'];

    // If rowData is provided, we're in edit mode
    if (rowData) {
      // Find the service in dataSource to get all subservices
      const serviceCode = rowData.code;
      const serviceName = rowData.service;
      const purpose = rowData.purpose;

      const serviceData = this.dataSource.data.find(item =>
        item.serviceObj && item.serviceObj.code === serviceCode && item.serviceObj.service === serviceName && item.serviceObj.purpose === purpose
      );

      if (serviceData) {
        // Pass the entire service data for editing along with master data
        const formValue = this.portForm.value;
        dialogConfig.data = {
          ...serviceData,
          index,
          masterData: this.masterData,
          ...formValue,
          vessel_fields: Array.isArray(formValue.vessel_fields) ? formValue.vessel_fields : [],
          existingServices: this.dataSource.data,
          originalServiceIndex: this.dataSource.data.findIndex(item => item.serviceObj && item.serviceObj.code === serviceCode && item.serviceObj.service === serviceName && item.serviceObj.purpose === purpose) // Pass index for edit identification
        };
      } else {
        // Fallback to just the row data along with master data
        const formValue = this.portForm.value;
        dialogConfig.data = {
          ...rowData,
          index,
          masterData: this.masterData,
          ...formValue,
          vessel_fields: Array.isArray(formValue.vessel_fields) ? formValue.vessel_fields : [],
          existingServices: this.dataSource.data,
          originalServiceIndex: this.dataSource.data.findIndex(item => item.serviceObj && item.serviceObj.code === serviceCode && item.serviceObj.service === serviceName && item.serviceObj.purpose === purpose) // Pass index for edit identification
        };
      }
    }
    else {
      // For add mode, pass master data along with form data (including vessel_fields)
      const formValue = this.portForm.value;
      dialogConfig.data = {
        masterData: this.masterData,
        ...formValue,
        vessel_fields: Array.isArray(formValue.vessel_fields) ? formValue.vessel_fields : [],
        existingServices: this.dataSource.data
      };
    }
    const dialogRef = this.dialog.open(CreateTariffComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data && result.data.tariffRequest) {
        const tariffService = result.data.tariffRequest.items[0];
        const serviceCode = result.data.tariffRequest.items[0].code;
        const serviceName = result.data.tariffRequest.items[0].service;
        const purpose = result.data.tariffRequest.items[0].purpose;
        const total = result.data.tariffRequest.items[0].total;
        // Check if service already exists by name only
        const existingServiceIndex = this.dataSource.data.findIndex(item =>
          item.serviceObj && item.serviceObj.service === serviceName && item.serviceObj.purpose === purpose
        );

        if (existingServiceIndex !== -1 && !result.data.isEdit) {
          // Service exists, check for duplicate subservices by name only
          const existingSubServices = this.dataSource.data[existingServiceIndex].serviceObj.sub_services;
          const newSubServices = tariffService.subService;

          const duplicates = newSubServices.filter((newSub: any) =>
            existingSubServices.some((existingSub: any) =>
              existingSub.unique_key === newSub.unique_key
            )
          );

          if (duplicates.length > 0) {
            this.snackBar.error(`Subservice '${duplicates[0].name}' already exists in ${serviceName}`);
            return;
          }

          // Merge subservices
          const mergedSubServices = [...existingSubServices, ...newSubServices.map((sub: any) =>
          ({
            name: sub.name || '',
            basic_value: sub.basic_value,
            tariff_percent: sub.tariff_percent,
            movement: sub.movement,
            formula_result: sub.formula_result,
            optional: sub.optional === 'Y',
            hide: sub.hide === 'Y',
            sub_total: 0,
            operational_flag: '+'
          })


          )];
          const updatedServiceObj = {
            ...this.dataSource.data[existingServiceIndex].serviceObj,
            sub_services: mergedSubServices
          };

          const updatedData = [...this.dataSource.data];
          updatedData[existingServiceIndex] = { serviceObj: updatedServiceObj };
          this.dataSource.data = updatedData;
          this.regenerateSNOs();
          this.updateTable();
          this.snackBar.success('Subservice added to existing service successfully');
          return;
        }

        // New service or edit mode - Convert TariffRequest back to legacy format for existing UI
        let sno: number;
        if (result.data.isEdit) {
          // For edit mode, find the original index to maintain position
          const existingServiceIndex = this.dataSource.data.findIndex(item =>
            item.serviceObj && item.serviceObj.code === serviceCode && item.serviceObj.service === serviceName && item.serviceObj.purpose === purpose
          );
          sno = existingServiceIndex !== -1 ? existingServiceIndex + 1 : this.dataSource.data.length + 1;
        } else {
          // For new service, add at the end
          sno = this.dataSource.data.length + 1;
        }

        const serviceObj = {
          SNO: sno,
          code: serviceCode,
          total: String(total),
          service: serviceName,
          purpose: purpose || null,
          pa_remark: tariffService.pa_remark || null,
          meraki_feedback: tariffService.meraki_feedback || null,
          meraki_remark_client: tariffService.meraki_remark_client || null,
          client_comment: tariffService.client_comment || null,
          client_option: tariffService.client_option || null,
          info_msg: tariffService.info_msg || null,
          negotiate: tariffService.negotiate || 'N',
          agreed: tariffService.agreed || 'N',
          sub_services: tariffService.subService.map((sub: any) => ({
            name: sub.name || null,
            unique_key: sub.unique_key || null,
            basic_value: sub.basic_value || null,
            movement: sub.movement || null,
            tariff_percent: sub.tariff_percent || null,
            calculated_basic_value: null,
            formula_result: sub.formula_result || null,
            formula_inputs: null,
            optional: sub.optional === 'Y',
            hide: sub.hide === 'Y',
            operational_flag: sub.operational_flag || '+',
            sub_total: 0
          }))
        };

        if (result.data.isEdit) {
          // Handle edit mode - use original service index
          const serviceIndex = result.data.originalServiceIndex;
          if (serviceIndex !== undefined && serviceIndex !== -1 && serviceIndex < this.dataSource.data.length) {
            const updatedData = [...this.dataSource.data];
            updatedData[serviceIndex] = { serviceObj };
            this.dataSource.data = updatedData;
            this.regenerateSNOs();
            this.updateTable();
          }
          this.snackBar.success('Tariff updated successfully');
        } else {
          // Handle add mode
          const newData = [...this.dataSource.data, { serviceObj }];
          this.dataSource.data = newData;
          this.regenerateSNOs();
          this.updateTable();
          this.snackBar.success('Tariff added successfully');
        }
      }
    });

  }
  onManualScroll() {
    // Empty method for scroll event handling
  }

  getServiceRowSpan(element: any, index: number): number {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;

    if (index === 0 || prevKey !== currentKey) {
      // First occurrence of this service+purpose combination
      let count = 1;
      for (let i = index + 1; i < data.length; i++) {
        const nextKey = `${data[i].service}|${data[i].purpose || ''}`;
        if (nextKey === currentKey) {
          count++;
        } else {
          break;
        }
      }
      return count;
    }
    return 0; // Not the first occurrence, don't show
  }

  getCodeRowSpan(element: any, index: number): number {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;

    if (index === 0 || prevKey !== currentKey) {
      // First occurrence of this service+purpose combination
      let count = 1;
      for (let i = index + 1; i < data.length; i++) {
        const nextKey = `${data[i].service}|${data[i].purpose || ''}`;
        if (nextKey === currentKey) {
          count++;
        } else {
          break;
        }
      }
      return count;
    }
    return 0; // Not the first occurrence, don't show
  }

  shouldShowServiceCell(element: any, index: number): boolean {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;
    return index === 0 || prevKey !== currentKey;
  }

  shouldShowCodeCell(element: any, index: number): boolean {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;
    return index === 0 || prevKey !== currentKey;
  }

  getActionRowSpan(element: any, index: number): number {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;

    if (index === 0 || prevKey !== currentKey) {
      // First occurrence of this service+purpose combination
      let count = 1;
      for (let i = index + 1; i < data.length; i++) {
        const nextKey = `${data[i].service}|${data[i].purpose || ''}`;
        if (nextKey === currentKey) {
          count++;
        } else {
          break;
        }
      }
      return count;
    }
    return 0; // Not the first occurrence, don't show
  }

  shouldShowActionCell(element: any, index: number): boolean {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;
    return index === 0 || prevKey !== currentKey;
  }

  getInfoRowSpan(element: any, index: number): number {
    // info_msg is tied to service/purpose; span across grouped rows
    return this.getServiceRowSpan(element, index);
  }

  shouldShowInfoCell(element: any, index: number): boolean {
    return this.shouldShowServiceCell(element, index);
  }

  dropRow(event: CdkDragDrop<any[]>): void {
    if (!event || !this.tableDataSource?.data || this.tableDataSource.data.length === 0) {
      return;
    }
    const reordered = [...this.tableDataSource.data];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    this.tableDataSource.data = reordered;
    // Keep SNO aligned with current order
    this.regenerateSNOs();
    // Notify the table to re-render
    this.tableDataSource._updateChangeSubscription();
  }

  getSNORowSpan(element: any, index: number): number {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;

    if (index === 0 || prevKey !== currentKey) {
      // First occurrence of this service+purpose combination
      let count = 1;
      for (let i = index + 1; i < data.length; i++) {
        const nextKey = `${data[i].service}|${data[i].purpose || ''}`;
        if (nextKey === currentKey) {
          count++;
        } else {
          break;
        }
      }
      return count;
    }
    return 0; // Not the first occurrence, don't show
  }

  shouldShowSNOCell(element: any, index: number): boolean {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;
    return index === 0 || prevKey !== currentKey;
  }

  getDragHandleRowSpan(element: any, index: number): number {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;

    if (index === 0 || prevKey !== currentKey) {
      // First occurrence of this service+purpose combination
      let count = 1;
      for (let i = index + 1; i < data.length; i++) {
        const nextKey = `${data[i].service}|${data[i].purpose || ''}`;
        if (nextKey === currentKey) {
          count++;
        } else {
          break;
        }
      }
      return count;
    }
    return 0; // Not the first occurrence, don't show
  }

  shouldShowDragHandleCell(element: any, index: number): boolean {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;
    return index === 0 || prevKey !== currentKey;
  }

  getPurposeRowSpan(element: any, index: number): number {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;

    if (index === 0 || prevKey !== currentKey) {
      // First occurrence of this service+purpose combination
      let count = 1;
      for (let i = index + 1; i < data.length; i++) {
        const nextKey = `${data[i].service}|${data[i].purpose || ''}`;
        if (nextKey === currentKey) {
          count++;
        } else {
          break;
        }
      }
      return count;
    }
    return 0; // Not the first occurrence, don't show
  }

  shouldShowPurposeCell(element: any, index: number): boolean {
    const data = this.tableDataSource.data;
    const currentKey = `${element.service}|${element.purpose || ''}`;
    const prevKey = index > 0 ? `${data[index - 1].service}|${data[index - 1].purpose || ''}` : null;
    return index === 0 || prevKey !== currentKey;
  }

  deleteService(element: any, index: number) {
    const serviceName = element.service;
    const serviceCode = element.code;
    const purpose = element.purpose;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Delete Service',
        text: `Are you sure you want to delete the service '${serviceName}'${purpose ? ` with purpose '${purpose}'` : ''}? This will remove all its subservices.`
      }
    });


    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Find and remove the service from dataSource using service+purpose combination
        const serviceIndex = this.dataSource.data.findIndex(item =>
          item.serviceObj &&
          item.serviceObj.code === serviceCode &&
          item.serviceObj.service === serviceName &&
          item.serviceObj.purpose === (purpose || null)
        );

        if (serviceIndex !== -1) {
          const updatedData = [...this.dataSource.data];
          updatedData.splice(serviceIndex, 1);
          this.dataSource.data = updatedData;
          this.regenerateSNOs();
          this.updateTable();
          this.snackBar.success(`Service '${serviceName}' deleted successfully`);
        }
      }
    });
  }

  drop(event: CdkDragDrop<any[]>) {
    const draggedRow = this.tableDataSource.data[event.previousIndex];
    const draggedKey = `${draggedRow.service}|${draggedRow.purpose || ''}`;
    const draggedIndex = this.dataSource.data.findIndex(s => `${s.serviceObj.service}|${s.serviceObj.purpose || ''}` === draggedKey);

    let targetIndex;
    if (event.currentIndex >= this.tableDataSource.data.length) {
      targetIndex = this.dataSource.data.length;
    } else {
      const targetRow = this.tableDataSource.data[event.currentIndex];
      const targetKey = `${targetRow.service}|${targetRow.purpose || ''}`;
      targetIndex = this.dataSource.data.findIndex(s => `${s.serviceObj.service}|${s.serviceObj.purpose || ''}` === targetKey);
    }

    // Move the dragged service to the target position
    const [draggedService] = this.dataSource.data.splice(draggedIndex, 1);
    this.dataSource.data.splice(targetIndex, 0, draggedService);

    this.updateTable();
  }

  updateTable() {
    this.tableDataSource.data = this.dataSource.data.flatMap((item, groupIndex) => {
      const subServices = item.serviceObj.sub_services || item.serviceObj.subService || [];

      return subServices.map((sub: any) => ({
        ...sub,
        groupClass: groupIndex % 2 === 0 ? 'service-group-even' : 'service-group-odd',
        code: item.serviceObj.code,
        service: item.serviceObj.service,
        sno: item.serviceObj.SNO,
        optional: sub.optional === 'Y' || sub.optional === true,
        formula_result: sub.resultformula || sub.formula_result,
        hide: sub.hide === 'Y' || sub.hide === true,
        purpose: item.serviceObj.purpose,
        info_msg: item.serviceObj.info_msg,
      }));
    });

  }



  splitDataType(data: string): { type: string, data: string } {
    // Split the input string into two parts based on the colon ":"
    if (data.includes(':')) {
      const [type, dataValue] = data?.split(':');
      return {
        type: type.trim(),
        data: dataValue.trim()
      };
    }
    // if the data does not contain the  : then return data
    return {
      type: data,
      data: ''
    }

  }
}
